import { useRef, useState } from "react";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Download, Copy, FileCode2, Link as LinkIcon, Contact } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { buildVCard } from "@/lib/vcard";

export default function QRDialog({ card, open, onOpenChange }) {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const [mode, setMode] = useState("vcard");
  const url = `${window.location.origin}/card/${card.slug}`;
  const vcard = buildVCard(card);
  const qrValue = mode === "vcard" ? vcard : url;

  const downloadPng = () => {
    const canvas = wrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${card.slug}-${mode}-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const downloadSvg = () => {
    const svg = svgRef.current?.querySelector("svg");
    if (!svg) return;

    const svgMarkup = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${svgMarkup}`], {
      type: "image/svg+xml;charset=utf-8",
    });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = `${card.slug}-${mode}-qr.svg`;
    link.href = objectUrl;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#101B28] border-[#1E2D40] text-white max-w-md" data-testid="qr-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">QR code</DialogTitle>
        </DialogHeader>

        <Tabs value={mode} onValueChange={setMode} className="w-full">
          <TabsList className="bg-[#0B131E] border border-[#1E2D40] w-full grid grid-cols-2">
            <TabsTrigger value="vcard" data-testid="qr-tab-vcard" className="gap-2">
              <Contact className="w-3.5 h-3.5" /> vCard (Contact)
            </TabsTrigger>
            <TabsTrigger value="url" data-testid="qr-tab-url" className="gap-2">
              <LinkIcon className="w-3.5 h-3.5" /> Public URL
            </TabsTrigger>
          </TabsList>

          <TabsContent value="vcard" className="mt-4">
            <p className="text-xs text-[#94A3B8] text-center mb-3">
              Scanning triggers the phone&apos;s native &ldquo;Add Contact&rdquo; popup.
            </p>
          </TabsContent>
          <TabsContent value="url" className="mt-4">
            <p className="text-xs text-[#94A3B8] text-center mb-3">
              Scanning opens the public card page in the browser.
            </p>
          </TabsContent>
        </Tabs>

        <div className="flex flex-col items-center gap-4 py-2">
          <div ref={wrapRef} className="p-4 rounded-2xl bg-white" data-testid="qr-canvas">
            <QRCodeCanvas
              value={qrValue}
              size={240}
              bgColor="#ffffff"
              fgColor="#07111D"
              level="L"
              includeMargin={false}
            />
          </div>
          <div ref={svgRef} className="hidden" aria-hidden="true">
            <QRCodeSVG
              value={qrValue}
              size={240}
              bgColor="#ffffff"
              fgColor="#07111D"
              level="L"
              includeMargin={false}
            />
          </div>
          <div className="text-sm text-[#94A3B8] text-center break-all w-full">
            {mode === "url" ? (
              <>
                <div className="text-xs uppercase tracking-[0.2em] text-[#4A5A73] mb-1">Public URL</div>
                <code className="text-[#18BFFF]" data-testid="qr-url">{url}</code>
              </>
            ) : (
              <>
                <div className="text-xs uppercase tracking-[0.2em] text-[#4A5A73] mb-1">Encoded contact</div>
                <code className="text-[#18BFFF]" data-testid="qr-contact">
                  {card.full_name || card.slug}
                </code>
              </>
            )}
          </div>
        </div>

        <DialogFooter className="grid w-full min-w-0 grid-cols-1 gap-2 sm:grid-cols-3">
          <Button
            variant="outline"
            onClick={copyLink}
            data-testid="qr-copy-link"
            className="min-w-0 px-2 text-xs border-[#1E2D40] bg-transparent text-white hover:bg-[#162436] hover:text-white sm:text-sm"
          >
            <Copy className="w-4 h-4 shrink-0 sm:mr-2" />
            Copy link
          </Button>
          <Button
            variant="outline"
            onClick={downloadSvg}
            data-testid="qr-download-svg-btn"
            className="min-w-0 px-2 text-xs border-[#1E2D40] bg-transparent text-white hover:bg-[#162436] hover:text-white sm:text-sm"
          >
            <Download className="w-4 h-4 shrink-0 sm:mr-2" />
            SVG
          </Button>
          <Button
            onClick={downloadPng}
            data-testid="qr-download-btn"
            className="min-w-0 px-2 text-xs bg-[#18BFFF] text-[#07111D] hover:bg-[#4DD0FF] sm:text-sm"
          >
            <Download className="w-4 h-4 shrink-0 sm:mr-2" />
            PNG
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
